// Constants (BASE_SALARY is now passed as parameter)
const STATE_CITY_TAX_RATE = 0.0965;
const NIIT_THRESHOLD = 200000;
const NIIT_RATE = 0.038;

// Tax calculation functions
function calculateFederalTax(income) {
  if (income <= 11600) return 0;
  if (income <= 47150) return (income - 11600) * 0.12;
  if (income <= 100525) return 4263 + (income - 47150) * 0.22;
  if (income <= 191950) return 14751 + (income - 100525) * 0.24;
  if (income <= 243725) return 33603 + (income - 191950) * 0.32;
  if (income <= 609350) return 47843 + (income - 243725) * 0.35;
  return 157804 + (income - 609350) * 0.37;
}

function calculateTax(grossGainThisWeek, annualGrossGains, baseSalary, currentYear) {
  const totalAnnualIncome = baseSalary + annualGrossGains;
  
  // Calculate federal tax on total income vs base salary to get marginal rate effect
  const federalTaxOnTotal = calculateFederalTax(totalAnnualIncome);
  const federalTaxOnBase = calculateFederalTax(baseSalary);
  const federalTaxOnGains = federalTaxOnTotal - federalTaxOnBase;
  
  // Calculate weekly federal tax portion
  const weeklyFederalTax = annualGrossGains > 0 ? 
    (federalTaxOnGains * grossGainThisWeek) / annualGrossGains : 0;
  
  // State and city tax on this week's gain
  const stateCityTax = grossGainThisWeek * STATE_CITY_TAX_RATE;
  
  // NIIT applies if annual income exceeds threshold
  let niitTax = 0;
  if (totalAnnualIncome > NIIT_THRESHOLD) {
    niitTax = grossGainThisWeek * NIIT_RATE;
  }
  
  const totalTax = weeklyFederalTax + stateCityTax + niitTax;
  
  return {
    total: totalTax,
    federal: weeklyFederalTax,
    stateCity: stateCityTax,
    niit: niitTax,
    effectiveRate: grossGainThisWeek > 0 ? (totalTax / grossGainThisWeek) * 100 : 0
  };
}

// Helper function to get next Friday from a given date
function getNextFriday(date) {
  const nextFriday = new Date(date);
  const daysUntilFriday = (5 - date.getDay() + 7) % 7; // 5 = Friday, 0 = Sunday
  if (daysUntilFriday === 0 && date.getDay() !== 5) {
    // If it's not Friday but calculation says 0 days, we need 7 days
    nextFriday.setTime(date.getTime() + 7 * 24 * 60 * 60 * 1000);
  } else if (daysUntilFriday > 0) {
    nextFriday.setTime(date.getTime() + daysUntilFriday * 24 * 60 * 60 * 1000);
  }
  return nextFriday;
}

// Helper function to ensure a date is a Friday (if not, get the next Friday)
function ensureFriday(date) {
  if (date.getDay() === 5) { // Already a Friday
    return new Date(date);
  }
  return getNextFriday(date);
}

// Main data generation function
export function generateInvestmentData(initialInvestment, targetNetValue = 2000000, endDate = new Date(2029, 11, 28), startDate = new Date(2025, 0, 1), baseSalary = 0) {
  const data = [];
  let cumulativeGross = initialInvestment;
  let cumulativeNet = initialInvestment;
  let currentYear = startDate.getFullYear();
  let weekInYear = Math.ceil((startDate.getTime() - new Date(startDate.getFullYear(), 0, 1).getTime()) / (1000 * 60 * 60 * 24 * 7));
  let yearlyGrossGains = 0;
  let yearlyNetGains = 0;
  let yearlyTotalTax = 0;
  let yearlyFederalTax = 0;
  let yearlyStateCityTax = 0;
  let yearlyNIITTax = 0;
  let pendingTaxPayment = 0;
  
  // Monthly tracking for 60/40 split
  let monthlyNetGains = 0;
  let currentMonth = startDate.getMonth();
  let currentMonthYear = startDate.getFullYear();
  
  // Calculate total weeks from start date to end date
  let totalWeeks = 0;
  let currentDate = ensureFriday(startDate); // Ensure we start on a Friday
  
  // Count total weeks to end date
  while (currentDate <= endDate) {
    totalWeeks++;
    currentDate = new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000);
  }
  
  // Calculate growth rates based on target and initial investment
  const requiredTotalReturn = targetNetValue / initialInvestment;
  const weeklyGrowthRate = Math.pow(requiredTotalReturn, 1/totalWeeks) - 1;
  
  // Reset for actual calculation - ensure we start on a Friday
  currentDate = ensureFriday(startDate);
  
  for (let week = 1; week <= totalWeeks; week++) {
    // Check if we've moved to a new year
    const nextWeekDate = new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000);
    const isYearEnd = currentDate.getFullYear() !== nextWeekDate.getFullYear() || week === totalWeeks;
    
    // Check if we've moved to a new month
    const isMonthEnd = currentDate.getMonth() !== nextWeekDate.getMonth() || 
                     currentDate.getFullYear() !== nextWeekDate.getFullYear() || 
                     week === totalWeeks;
    
    // Check if we need to pay taxes (Friday before April 15th) - for all years
    const april15 = new Date(currentDate.getFullYear(), 3, 15); // April 15th
    const fridayBeforeApril15 = new Date(april15);
    const daysToSubtract = ((april15.getDay() + 2) % 7);
    fridayBeforeApril15.setTime(april15.getTime() - daysToSubtract * 24 * 60 * 60 * 1000); // Get Friday before
    
    const isTaxPaymentWeek = currentDate.getTime() === fridayBeforeApril15.getTime() && 
                           pendingTaxPayment > 0;
    
    if (currentDate.getFullYear() > currentYear) {
      currentYear = currentDate.getFullYear();
      weekInYear = 1;
      yearlyGrossGains = 0;
      yearlyNetGains = 0;
      yearlyTotalTax = 0;
      yearlyFederalTax = 0;
      yearlyStateCityTax = 0;
      yearlyNIITTax = 0;
      currentMonth = 0; // January = 0
      currentMonthYear = currentYear;
      // Reset monthly tracking when starting a new year
      monthlyNetGains = 0;
    } else if (week > 1) {
      weekInYear++;
    }
    
    // Check if we've moved to a new month
    if (currentDate.getMonth() !== currentMonth || currentDate.getFullYear() !== currentMonthYear) {
      currentMonth = currentDate.getMonth();
      currentMonthYear = currentDate.getFullYear();
      // Reset monthly tracking when moving to a new month
      monthlyNetGains = 0;
    }
    
    // Pay taxes if it's tax payment week (for all years)
    if (isTaxPaymentWeek) {
      cumulativeGross -= pendingTaxPayment;
      pendingTaxPayment = 0;
    }
    
    // Target net value for this week
    const weeklyTargetNetValue = initialInvestment * Math.pow(1 + weeklyGrowthRate, week);
    const netGainThisWeek = weeklyTargetNetValue - cumulativeNet;
    
    // Calculate gross gain needed - iterative approach for accuracy
    let grossGainThisWeek = netGainThisWeek / 0.63; // Initial estimate
    
    // Apply tax calculations for all years
    // Refine calculation with actual tax calculation (5 iterations)
    for (let i = 0; i < 5; i++) {
      const tempYearlyGains = yearlyGrossGains + grossGainThisWeek;
      const tax = calculateTax(grossGainThisWeek, tempYearlyGains, baseSalary, currentYear);
      const actualNetGain = grossGainThisWeek - tax.total;
      const adjustment = netGainThisWeek - actualNetGain;
      grossGainThisWeek += adjustment / (1 - tax.effectiveRate / 100);
    }
    
    // Update yearly gains
    yearlyGrossGains += grossGainThisWeek;
    
    // Final tax calculation
    const tax = calculateTax(grossGainThisWeek, yearlyGrossGains, baseSalary, currentYear);
    const actualNetGain = grossGainThisWeek - tax.total;
    
    // Update cumulative values
    cumulativeGross += grossGainThisWeek;
    cumulativeNet += actualNetGain;
    
    // Update yearly tax totals
    yearlyNetGains += actualNetGain;
    yearlyTotalTax += tax.total;
    yearlyFederalTax += tax.federal;
    yearlyStateCityTax += tax.stateCity;
    yearlyNIITTax += tax.niit;
    
    // Update monthly tracking
    monthlyNetGains += actualNetGain;
    
    // Calculate percentages
    const grossGainPercent = week === 1 ? 
      (grossGainThisWeek / initialInvestment) * 100 : 
      (grossGainThisWeek / (cumulativeGross - grossGainThisWeek)) * 100;
    
    const netGainPercent = week === 1 ? 
      (actualNetGain / initialInvestment) * 100 : 
      (actualNetGain / (cumulativeNet - actualNetGain)) * 100;
    
    // Calculate your 60% share after tax for the month
    // For regular weeks, show 60% of this week's net gain
    // For month-end weeks, show 60% of accumulated monthly net gains
    const yourMonthlyShare = isMonthEnd ? monthlyNetGains * 0.6 : actualNetGain * 0.6;
    
    // Calculate the 40% share for reinvestment/other purposes
    // For regular weeks, show 40% of this week's net gain
    // For month-end weeks, show 40% of accumulated monthly net gains
    const reinvestmentShare = isMonthEnd ? monthlyNetGains * 0.4 : actualNetGain * 0.4;
    
    // Calculate the 30% share
    // For regular weeks, show 30% of this week's net gain
    // For month-end weeks, show 30% of accumulated monthly net gains
    const thirtyPercentShare = isMonthEnd ? monthlyNetGains * 0.3 : actualNetGain * 0.3;
    
    // Calculate the 50% share
    // For regular weeks, show 50% of this week's net gain
    // For month-end weeks, show 50% of accumulated monthly net gains
    const fiftyPercentShare = isMonthEnd ? monthlyNetGains * 0.5 : actualNetGain * 0.5;
    
    // Calculate the 70% share
    // For regular weeks, show 70% of this week's net gain
    // For month-end weeks, show 70% of accumulated monthly net gains
    const seventyPercentShare = isMonthEnd ? monthlyNetGains * 0.7 : actualNetGain * 0.7;
    
    data.push({
      week: weekInYear,
      date: new Date(currentDate),
      year: currentYear,
      cumulativeGross: cumulativeGross,
      grossGain: grossGainThisWeek,
      grossGainPercent: grossGainPercent,
      cumulativeNet: cumulativeNet,
      netGain: actualNetGain,
      netGainPercent: netGainPercent,
      yearlyGrossGains: yearlyGrossGains,
      yearlyNetGains: yearlyNetGains,
      totalTaxableIncome: baseSalary + yearlyGrossGains,
      tax: tax,
      thirtyPercentShare: thirtyPercentShare,
      yourMonthlyShare: yourMonthlyShare,
      reinvestmentShare: reinvestmentShare,
      fiftyPercentShare: fiftyPercentShare,
      seventyPercentShare: seventyPercentShare,
      isWeeklyData: true,
      isTaxPaymentWeek: isTaxPaymentWeek,
      taxPaymentAmount: isTaxPaymentWeek ? pendingTaxPayment : 0,
      isMonthEnd: isMonthEnd
    });
    
    // Add year-end summary row only for the final year
    if (isYearEnd) {
      const yearlyNetGainPercent = cumulativeNet > initialInvestment ? 
        (yearlyNetGains / (cumulativeNet - yearlyNetGains)) * 100 : 0;
      
      // Set pending tax payment for next year's April 15th (for all years)
      pendingTaxPayment = yearlyTotalTax;
      
      // Only add year summary for the final year
      if (currentYear === endDate.getFullYear()) {
        // Calculate total accumulated amounts for the entire investment period
        const totalAccumulatedNetGain = cumulativeNet - initialInvestment;
        const totalAccumulated30Percent = totalAccumulatedNetGain * 0.3;
        const totalAccumulated40Percent = totalAccumulatedNetGain * 0.4;
        const totalAccumulated50Percent = totalAccumulatedNetGain * 0.5;
        const totalAccumulated60Percent = totalAccumulatedNetGain * 0.6;
        const totalAccumulated70Percent = totalAccumulatedNetGain * 0.7;
        
        data.push({
          week: null,
          date: currentDate,
          year: currentYear,
          cumulativeGross: null,
          grossGain: yearlyGrossGains,
          grossGainPercent: null,
          cumulativeNet: null,
          netGain: yearlyNetGains,
          netGainPercent: yearlyNetGainPercent,
          yearlyGrossGains: null,
          totalTaxableIncome: null,
          tax: {
            total: yearlyTotalTax,
            federal: yearlyFederalTax,
            stateCity: yearlyStateCityTax,
            niit: yearlyNIITTax,
            effectiveRate: yearlyGrossGains > 0 ? (yearlyTotalTax / yearlyGrossGains) * 100 : 0
          },
          thirtyPercentShare: totalAccumulated30Percent,
          yourMonthlyShare: totalAccumulated60Percent,
          reinvestmentShare: totalAccumulated40Percent,
          fiftyPercentShare: totalAccumulated50Percent,
          seventyPercentShare: totalAccumulated70Percent,
          isWeeklyData: false,
          isYearSummary: true
        });
      } else {
        // Add subtle year divider for other years
        const yearlyNetGains = yearlyGrossGains - yearlyTotalTax;
        const yearlyNetGainPercent = cumulativeNet > initialInvestment ? 
          (yearlyNetGains / (cumulativeNet - yearlyNetGains)) * 100 : 0;
        
        data.push({
          week: null,
          date: currentDate,
          year: currentYear,
          cumulativeGross: null,
          grossGain: null,
          grossGainPercent: null,
          cumulativeNet: null,
          netGain: yearlyNetGains,
          netGainPercent: yearlyNetGainPercent,
          yearlyGrossGains: yearlyGrossGains, // Store for tooltip reference
          totalTaxableIncome: null,
          tax: {
            total: yearlyTotalTax,
            federal: yearlyFederalTax,
            stateCity: yearlyStateCityTax,
            niit: yearlyNIITTax,
            effectiveRate: yearlyGrossGains > 0 ? (yearlyTotalTax / yearlyGrossGains) * 100 : 0
          },
          isWeeklyData: false,
          isYearSummary: false,
          isYearDivider: true
        });
      }
    }
    
    // Move to next Friday (more reliable method)
    currentDate = new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    // Break if we've reached the end date
    if (currentDate > endDate) break;
  }
  
  return data;
}
